import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Modal,
  TextField,
  Stack,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import Loader from '../../../../components/loader';
import AlertToast from '../../../../components/alertToast';
import { appCatalogueGetById, getAppServices, appServiceCreateUpdate, deleteAppService, uploadAppServiceLogo, patchAppServiceStatus } from '../appCatalogue.service';
import { AntSwitch } from '../../../../components/antSwitch';
import { appendAzureSasToken } from '../../../../../service/azure.utils';
import {
  detailFieldSx,
  detailLabelSx,
  detailPageCardSx as pageCardSx,
  detailSectionTitleSx,
  detailValueSx,
} from '../../../../styles/detailStyles';
import {
  confirmationDialogSx,
  confirmationIconSx,
  confirmationPrimaryButtonSx,
  confirmationSecondaryButtonSx,
  confirmationTextSx,
  confirmationTitleSx,
} from '../../../../styles/dialogStyles';
import { FieldLabel, formFieldSx, helperTextSx } from '../../../../styles/formStyles';
import { sanitizeHtml } from '../../../../../helper/sanitizeHtml';

const ALERT_DURATION = 2000;

const appServiceSchema = z.object({
  code: z
    .string()
    .min(1, { message: 'Code is required' })
    .regex(/^[A-Z0-9]+(_[A-Z0-9]+)*$/, {
      message: 'Use UPPER_SNAKE_CASE, e.g. WALLET or CROP_ADVISORY',
    }),
  name: z.string().min(1, { message: 'Name is required' }),
  description: z.string().min(1, { message: 'Description is required' }),
  displayOrder: z.coerce
    .number({ invalid_type_error: 'Display order must be a number' })
    .int({ message: 'Display order must be a whole number' })
    .min(0, { message: 'Display order cannot be negative' }),
});

function DetailField({ label, value, fullWidth = false, minHeight, html = false }) {
  return (
    <Grid size={{ xs: 12, md: fullWidth ? 12 : 6 }}>
      <Box sx={{ ...detailFieldSx, minHeight: minHeight || detailFieldSx.minHeight }}>
        <Typography sx={detailLabelSx}>{label}</Typography>
        {html ? (
          <Box
            sx={{ ...detailValueSx, '& p': { my: 0 } }}
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(value) }}
          />
        ) : (
          <Typography sx={detailValueSx}>{value || '-'}</Typography>
        )}
      </Box>
    </Grid>
  );
}

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];

function AppServiceModal({ open, onClose, appCatalogueId, serviceData, onSuccess, onUploadError }) {
  const editMode = !!serviceData;
  const [logoUrl, setLogoUrl] = useState('');
  const [uploading, setUploading] = useState(false);

  const { handleSubmit, control, reset } = useForm({
    resolver: zodResolver(appServiceSchema),
    defaultValues: { code: '', name: '', description: '', displayOrder: 0 },
  });

  useEffect(() => {
    if (open) {
      reset(
        serviceData
          ? {
              code: serviceData.code || '',
              name: serviceData.name || '',
              description: serviceData.description || '',
              displayOrder: serviceData.displayOrder ?? 0,
            }
          : { code: '', name: '', description: '', displayOrder: 0 }
      );
      setLogoUrl(serviceData?.logoUrl || '');
    }
  }, [open, serviceData, reset]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      onUploadError('Only JPEG, PNG, WebP files are allowed.');
      e.target.value = '';
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      onUploadError('File size must be less than 10MB.');
      e.target.value = '';
      return;
    }

    try {
      setUploading(true);
      const url = await uploadAppServiceLogo(file);
      setLogoUrl(url);
    } catch {
      onUploadError('Logo upload failed.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const onSubmit = async (data) => {
    const res = await appServiceCreateUpdate(
      appCatalogueId,
      { ...data, logoUrl },
      editMode,
      serviceData?.id
    );
    onSuccess(res, editMode ? 'App service updated.' : 'App service created.');
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '92%', sm: 520 },
          maxHeight: '90vh',
          overflowY: 'auto',
          bgcolor: '#FFFFFF',
          borderRadius: '28px',
          boxShadow: '0px 24px 48px rgba(28, 27, 31, 0.18)',
          p: 3,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography sx={{ fontWeight: 700, fontSize: 20, color: '#1C1B1F' }}>
            {editMode ? 'Edit App Service' : 'Add App Service'}
          </Typography>
          <Button onClick={onClose} sx={{ minWidth: 'auto', p: 0.5, color: '#6B7280', borderRadius: '999px' }}>
            <Icon icon="mdi:close" width="20" height="20" />
          </Button>
        </Stack>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2.5}>
            <Box>
              <FieldLabel label="Code" required />
              <Controller
                name="code"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    fullWidth
                    placeholder="e.g., WALLET or CROP_ADVISORY"
                    error={!!error}
                    helperText={
                      error
                        ? error.message
                        : 'Stable identifier the app switches on. UPPER_SNAKE_CASE, unique within this app.'
                    }
                    sx={{ ...formFieldSx, ...helperTextSx }}
                  />
                )}
              />
            </Box>

            <Box>
              <FieldLabel label="Name" required />
              <Controller
                name="name"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    fullWidth
                    placeholder="Enter service name"
                    error={!!error}
                    helperText={error ? error.message : ''}
                    sx={{ ...formFieldSx, ...helperTextSx }}
                  />
                )}
              />
            </Box>

            <Box>
              <FieldLabel label="Logo" />
              <Box
                sx={{
                  border: '1px dashed #D9E2F4',
                  borderRadius: '12px',
                  backgroundColor: '#FAFBFF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 100,
                  p: 2,
                  position: 'relative',
                }}
              >
                {uploading ? (
                  <Stack alignItems="center" spacing={1}>
                    <Icon icon="mdi:loading" width="28" height="28" color="#294190" style={{ animation: 'spin 1s linear infinite' }} />
                    <Typography sx={{ fontSize: 13, color: '#625B71' }}>Uploading…</Typography>
                  </Stack>
                ) : logoUrl ? (
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                    <Box
                      component="img"
                      src={appendAzureSasToken(logoUrl)}
                      alt="Logo preview"
                      sx={{ width: 64, height: 64, objectFit: 'contain', borderRadius: '8px', border: '1px solid #DFE5F6', flexShrink: 0 }}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                    <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, color: '#49454F', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        Logo uploaded
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => document.getElementById('app-service-logo-input')?.click()}
                        sx={{ alignSelf: 'flex-start', fontSize: 13, color: '#294190', textTransform: 'none', p: 0, minWidth: 'auto' }}
                      >
                        Replace
                      </Button>
                    </Stack>
                    <Button
                      onClick={() => setLogoUrl('')}
                      sx={{ minWidth: 'auto', p: 0.5, color: '#C40000', borderRadius: '999px', flexShrink: 0 }}
                    >
                      <Icon icon="mdi:close" width="18" height="18" />
                    </Button>
                  </Stack>
                ) : (
                  <Stack alignItems="center" spacing={1}>
                    <Typography sx={{ fontSize: 13, color: '#6C6A86' }}>JPEG, PNG, WebP · Max 10 MB</Typography>
                    <Button
                      onClick={() => document.getElementById('app-service-logo-input')?.click()}
                      sx={{
                        height: 36,
                        px: 2.5,
                        borderRadius: '10px',
                        border: '1px solid #294190',
                        color: '#294190',
                        textTransform: 'none',
                        fontSize: 14,
                        fontWeight: 500,
                      }}
                    >
                      Upload Logo
                    </Button>
                  </Stack>
                )}
              </Box>
              <input
                hidden
                type="file"
                id="app-service-logo-input"
                accept=".jpeg,.jpg,.png,.webp"
                onChange={handleFileChange}
              />
            </Box>

            <Box>
              <FieldLabel label="Description" required />
              <Controller
                name="description"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Write description here..."
                    error={!!error}
                    helperText={error ? error.message : ''}
                    sx={{ ...formFieldSx, ...helperTextSx }}
                  />
                )}
              />
            </Box>

            <Box>
              <FieldLabel label="Display Order" required />
              <Controller
                name="displayOrder"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    inputProps={{ min: 0, step: 1 }}
                    placeholder="0"
                    error={!!error}
                    helperText={
                      error ? error.message : 'Lower numbers appear first in the app login flow.'
                    }
                    sx={{ ...formFieldSx, ...helperTextSx }}
                  />
                )}
              />
            </Box>

            <Stack direction="row" spacing={1.5} justifyContent="flex-end" pt={1}>
              <Button onClick={onClose} sx={confirmationSecondaryButtonSx}>Cancel</Button>
              <Button variant="contained" type="submit" disabled={uploading} sx={confirmationPrimaryButtonSx}>
                {editMode ? 'Update' : 'Add'}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Box>
    </Modal>
  );
}

function DeleteServiceModal({ open, onClose, onConfirm }) {
  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={confirmationDialogSx}>
        <Stack direction="row" justifyContent="flex-end" mb={1}>
          <Button onClick={onClose} sx={{ minWidth: 'auto', p: 0.5, color: '#6B7280', borderRadius: '999px' }}>
            <Icon icon="mdi:close" width="20" height="20" />
          </Button>
        </Stack>
        <Stack spacing={2} alignItems="center" textAlign="center">
          <Box sx={confirmationIconSx}>
            <Icon icon="mingcute:warning-line" width="36" height="36" color="#C40000" />
          </Box>
          <Typography sx={confirmationTitleSx}>Delete App Service?</Typography>
          <Typography sx={confirmationTextSx}>This action will remove the app service.</Typography>
          <Stack direction="row" spacing={1.5} justifyContent="center" pt={1}>
            <Button variant="contained" onClick={onConfirm} sx={confirmationPrimaryButtonSx}>Delete</Button>
            <Button onClick={onClose} sx={confirmationSecondaryButtonSx}>Cancel</Button>
          </Stack>
        </Stack>
      </Box>
    </Modal>
  );
}

function ServiceStatusModal({ open, onClose, onConfirm, currentStatus }) {
  const isActive = currentStatus === 'ACTIVE';
  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={confirmationDialogSx}>
        <Stack direction="row" justifyContent="flex-end" mb={1}>
          <Button onClick={onClose} sx={{ minWidth: 'auto', p: 0.5, color: '#6B7280', borderRadius: '999px' }}>
            <Icon icon="mdi:close" width="20" height="20" />
          </Button>
        </Stack>
        <Stack spacing={2} alignItems="center" textAlign="center">
          <Box sx={confirmationIconSx}>
            <Icon icon="mingcute:warning-line" width="36" height="36" color="#C40000" />
          </Box>
          <Typography sx={confirmationTitleSx}>Warning!</Typography>
          <Typography sx={confirmationTextSx}>
            {isActive ? 'Deactivate this app service?' : 'Activate this app service?'}
          </Typography>
          <Stack direction="row" spacing={1.5} justifyContent="center" pt={1}>
            <Button variant="contained" onClick={onConfirm} sx={confirmationPrimaryButtonSx}>Yes</Button>
            <Button onClick={onClose} sx={confirmationSecondaryButtonSx}>No</Button>
          </Stack>
        </Stack>
      </Box>
    </Modal>
  );
}

const tableCellHeaderSx = {
  fontWeight: 600,
  fontSize: 13,
  color: '#49454F',
  borderBottom: '1px solid #DFE5F6',
  py: 1.5,
  px: 2,
};

const tableCellSx = {
  fontSize: 14,
  color: '#1C1B1F',
  borderBottom: '1px solid #F3F4F6',
  py: 1.5,
  px: 2,
};

const AppCatalogueView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [appData, setAppData] = useState({});
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [serviceModal, setServiceModal] = useState({ open: false, data: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, serviceId: null });
  const [statusModal, setStatusModal] = useState({ open: false, serviceId: null, currentStatus: null });
  const [openAlert, setOpenAlert] = useState(false);
  const [alert, setAlert] = useState('');
  const [responseMsg, setResponseMsg] = useState('');

  const alertClose = () => { setAlert(''); setOpenAlert(false); };

  const getAppCatalogueData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await appCatalogueGetById(id);
      setAppData(data?.data || {});
    } catch (error) {
      console.error('Error fetching app catalogue by ID:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchServices = useCallback(async () => {
    try {
      const res = await getAppServices(id);
      setServices((res?.data || []).filter((s) => s.status !== 'DELETED'));
    } catch {
      setServices([]);
    }
  }, [id]);

  useEffect(() => {
    getAppCatalogueData();
    fetchServices();
  }, [getAppCatalogueData, fetchServices]);

  const handleServiceSuccess = (res, successMsg) => {
    setResponseMsg(res?.message || successMsg);
    setAlert(res ? 'true' : 'false');
    setOpenAlert(true);
    if (res) {
      setServiceModal({ open: false, data: null });
      fetchServices();
    }
    setTimeout(alertClose, ALERT_DURATION);
  };

  const handleUploadError = (msg) => {
    setResponseMsg(msg);
    setAlert('false');
    setOpenAlert(true);
    setTimeout(alertClose, ALERT_DURATION);
  };

  const handleDeleteConfirm = async () => {
    const res = await deleteAppService(id, deleteModal.serviceId);
    setDeleteModal({ open: false, serviceId: null });
    setResponseMsg(res?.message || 'App service deleted.');
    setAlert(res ? 'true' : 'false');
    setOpenAlert(true);
    if (res) fetchServices();
    setTimeout(alertClose, ALERT_DURATION);
  };

  const handleStatusConfirm = async () => {
    const newStatus = statusModal.currentStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    const res = await patchAppServiceStatus(id, statusModal.serviceId, newStatus);
    setStatusModal({ open: false, serviceId: null, currentStatus: null });
    setResponseMsg(res?.message || 'Status updated.');
    setAlert(res ? 'true' : 'false');
    setOpenAlert(true);
    if (res) fetchServices();
    setTimeout(alertClose, ALERT_DURATION);
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: { xs: '100%', lg: 1360, xl: 1280 },
        boxSizing: 'border-box',
        mx: 'auto',
        px: { xs: 0, lg: 3, xl: 4 },
      }}
    >
      <Grid container alignItems="center" mb={4} rowSpacing={2} columnSpacing={2}>
        <Grid size={{ xs: 12, md: 3 }} textAlign={{ xs: 'left', md: 'left' }}>
          <Button
            onClick={() => navigate('/appCatalogue')}
            startIcon={<span className="material-symbols-rounded">arrow_back_ios_new</span>}
            sx={{
              minWidth: 'auto',
              px: 0,
              color: '#1C1B1F',
              textTransform: 'none',
              fontSize: 16,
              lineHeight: '24px',
              letterSpacing: '0.15px',
              fontWeight: 500,
              '& .material-symbols-rounded': { fontSize: 18 },
            }}
          >
            Back
          </Button>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }} order={{ xs: 3, md: 2 }}>
          <Typography
            className="app-page-title"
            sx={{
              textAlign: 'center',
              fontSize: { xs: 28, sm: 30, md: 34 },
              lineHeight: { xs: '34px', sm: '36px', md: '40px' },
              letterSpacing: '-0.25px',
              fontWeight: 700,
              color: '#1C1B1F',
            }}
          >
            App Details
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, md: 3 }} order={{ xs: 2, md: 3 }} textAlign={{ xs: 'left', md: 'right' }}>
          <Button
            onClick={() => navigate(`/addAppCatalogue/${id}?type=edit`)}
            startIcon={<span className="material-symbols-rounded">edit</span>}
            sx={{
              minWidth: { xs: '100%', sm: 86 },
              height: 40,
              borderRadius: '12px',
              backgroundColor: '#DFE5F6',
              color: '#294190',
              textTransform: 'none',
              fontSize: 16,
              lineHeight: '24px',
              letterSpacing: '0.15px',
              fontWeight: 500,
              '&:hover': { backgroundColor: '#DFE5F6' },
              '& .material-symbols-rounded': { fontSize: 18 },
            }}
          >
            Edit
          </Button>
        </Grid>
      </Grid>

      <Box sx={pageCardSx}>
        <Typography sx={detailSectionTitleSx}>App Information</Typography>

        <Grid container spacing={2.25}>
          <DetailField label="ID" value={appData?.id} />
          <DetailField label="App Key" value={appData?.appKey} />
          <DetailField label="App Name" value={appData?.name} />
          <DetailField label="URL" value={appData?.url} />
          <DetailField label="Status" value={appData?.status} />
          <DetailField label="Description" value={appData?.description} fullWidth minHeight={120} html />
        </Grid>
      </Box>

      <Box sx={{ ...pageCardSx, mt: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5}>
          <Typography sx={detailSectionTitleSx} mb={0}>App Services</Typography>
          <Button
            onClick={() => setServiceModal({ open: true, data: null })}
            startIcon={<Icon icon="mdi:plus" width="18" height="18" />}
            sx={{
              height: 40,
              px: 2,
              borderRadius: '12px',
              backgroundColor: '#294190',
              color: '#FFFFFF',
              textTransform: 'none',
              fontSize: 14,
              fontWeight: 500,
              '&:hover': { backgroundColor: '#1e3070' },
            }}
          >
            Add Service
          </Button>
        </Stack>

        {services.length === 0 ? (
          <Box
            sx={{
              border: '1px solid #DFE5F6',
              borderRadius: '16px',
              py: 5,
              textAlign: 'center',
            }}
          >
            <Typography sx={{ color: '#49454F', fontSize: 14 }}>No app services added yet.</Typography>
          </Box>
        ) : (
          <TableContainer sx={{ border: '1px solid #DFE5F6', borderRadius: '16px', overflow: 'hidden' }}>
            <Table>
              <TableHead sx={{ backgroundColor: '#F8F9FE' }}>
                <TableRow>
                  <TableCell sx={{ ...tableCellHeaderSx, textAlign: 'center' }}>Order</TableCell>
                  <TableCell sx={tableCellHeaderSx}>Logo</TableCell>
                  <TableCell sx={tableCellHeaderSx}>Code</TableCell>
                  <TableCell sx={tableCellHeaderSx}>Name</TableCell>
                  <TableCell sx={tableCellHeaderSx}>Description</TableCell>
                  <TableCell sx={tableCellHeaderSx}>Status</TableCell>
                  <TableCell sx={{ ...tableCellHeaderSx, textAlign: 'right' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {services.map((service) => (
                  <TableRow key={service.id} hover>
                    <TableCell sx={{ ...tableCellSx, textAlign: 'center', fontWeight: 500 }}>
                      {service.displayOrder ?? '-'}
                    </TableCell>
                    <TableCell sx={tableCellSx}>
                      {service.logoUrl ? (
                        <Box
                          component="img"
                          src={appendAzureSasToken(service.logoUrl)}
                          alt={service.name}
                          sx={{ width: 40, height: 40, objectFit: 'contain', borderRadius: '8px', border: '1px solid #DFE5F6' }}
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '8px',
                            border: '1px solid #DFE5F6',
                            backgroundColor: '#F8F9FE',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Icon icon="mdi:image-outline" width="20" height="20" color="#9CA3AF" />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell sx={tableCellSx}>
                      <Box
                        component="code"
                        sx={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: '#294190',
                          backgroundColor: '#EEF1FB',
                          borderRadius: '6px',
                          px: 1,
                          py: 0.5,
                        }}
                      >
                        {service.code || '-'}
                      </Box>
                    </TableCell>
                    <TableCell sx={tableCellSx}>{service.name}</TableCell>
                    <TableCell sx={{ ...tableCellSx, maxWidth: 320 }}>
                      <Typography
                        sx={{
                          fontSize: 14,
                          color: '#1C1B1F',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: 300,
                        }}
                      >
                        {service.description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={tableCellSx}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <AntSwitch
                          checked={service.status === 'ACTIVE'}
                          onChange={() => setStatusModal({ open: true, serviceId: service.id, currentStatus: service.status })}
                          inputProps={{ 'aria-label': 'service status' }}
                        />
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 500,
                            color: service.status === 'ACTIVE' ? '#4F8506' : '#625B71',
                          }}
                        >
                          {service.status === 'ACTIVE' ? 'Active' : 'Inactive'}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ ...tableCellSx, textAlign: 'right' }}>
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          onClick={() => setServiceModal({ open: true, data: service })}
                          sx={{
                            minWidth: 'auto',
                            px: 1,
                            py: 0.5,
                            borderRadius: '8px',
                            backgroundColor: '#DFE5F6',
                            color: '#294190',
                            '&:hover': { backgroundColor: '#c8d2f0' },
                          }}
                        >
                          <Icon icon="mdi:pencil-outline" width="18" height="18" />
                        </Button>
                        <Button
                          onClick={() => setDeleteModal({ open: true, serviceId: service.id })}
                          sx={{
                            minWidth: 'auto',
                            px: 1,
                            py: 0.5,
                            borderRadius: '8px',
                            backgroundColor: '#FDE1DC',
                            color: '#C40000',
                            '&:hover': { backgroundColor: '#f8c8c2' },
                          }}
                        >
                          <Icon icon="mdi:trash-can-outline" width="18" height="18" />
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <AppServiceModal
        open={serviceModal.open}
        onClose={() => setServiceModal({ open: false, data: null })}
        appCatalogueId={id}
        serviceData={serviceModal.data}
        onSuccess={handleServiceSuccess}
        onUploadError={handleUploadError}
      />

      <DeleteServiceModal
        open={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, serviceId: null })}
        onConfirm={handleDeleteConfirm}
      />

      <ServiceStatusModal
        open={statusModal.open}
        onClose={() => setStatusModal({ open: false, serviceId: null, currentStatus: null })}
        onConfirm={handleStatusConfirm}
        currentStatus={statusModal.currentStatus}
      />

      {loading && <Loader />}

      <AlertToast
        open={openAlert && alert === 'true'}
        onClose={alertClose}
        message={responseMsg}
        variant="success"
      />
      <AlertToast
        open={openAlert && alert === 'false'}
        onClose={alertClose}
        message={responseMsg}
        variant="error"
      />
    </Box>
  );
};

export default AppCatalogueView;
